import React, { useMemo } from 'react';
import { PaginationButton } from '../atoms';
import type { PaginationState } from '../../types/table';

/**
 * TablePagination Component - Molecule Level
 * 
 * Comprehensive pagination controls for DataTable
 * 
 * Features:
 * - Page navigation (first, prev, next, last)
 * - Page numbers with ellipsis
 * - Page size selector
 * - Item count display
 * - Responsive design
 * - Accessible (ARIA labels, keyboard navigation)
 */

export interface TablePaginationProps {
  /** Current pagination state */
  pagination: PaginationState;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Available page size options */
  pageSizeOptions?: number[];
  /** Show page size selector */
  showPageSize?: boolean;
  /** Show item count */
  showItemCount?: boolean;
  /** Maximum number of page buttons to show */
  maxPageButtons?: number;
  /** Additional CSS classes */
  className?: string;
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  pagination,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  showPageSize = true,
  showItemCount = true,
  maxPageButtons = 7,
  className = '',
}) => {
  const { page, pageSize, total, totalPages, hasNext, hasPrev } = pagination;

  /**
   * Calculate which page numbers to show
   * Uses ellipsis (...) for large page counts
   */
  const pageNumbers = useMemo(() => {
    if (totalPages <= maxPageButtons) {
      // Show all pages if total is less than max
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const halfMax = Math.floor(maxPageButtons / 2);

    // Always show first page
    pages.push(1);

    if (page <= halfMax + 1) {
      // Near the start
      for (let i = 2; i <= Math.min(maxPageButtons - 1, totalPages - 1); i++) {
        pages.push(i);
      }
      if (totalPages > maxPageButtons) {
        pages.push('...');
      }
    } else if (page >= totalPages - halfMax) {
      // Near the end
      pages.push('...');
      for (let i = totalPages - (maxPageButtons - 2); i < totalPages; i++) {
        if (i > 1) {
          pages.push(i);
        }
      }
    } else {
      // In the middle
      pages.push('...');
      for (let i = page - halfMax + 2; i <= page + halfMax - 2; i++) {
        pages.push(i);
      }
      pages.push('...');
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }, [page, totalPages, maxPageButtons]);

  /**
   * Calculate the range of items being displayed
   */
  const itemRange = useMemo(() => {
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return { start, end };
  }, [page, pageSize, total]);

  if (totalPages === 0) {
    return null;
  }

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
      {/* Item Count */}
      {showItemCount && (
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold text-gray-900">{itemRange.start}</span> to{' '}
          <span className="font-semibold text-gray-900">{itemRange.end}</span> of{' '}
          <span className="font-semibold text-gray-900">{total}</span> results
        </div>
      )}

      {/* Pagination Controls */}
      <div className="flex items-center gap-2">
        {/* First Page */}
        <PaginationButton
          variant="nav"
          onClick={() => onPageChange(1)}
          disabled={!hasPrev}
          ariaLabel="Go to first page"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
        </PaginationButton>

        {/* Previous Page */}
        <PaginationButton
          variant="nav"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
          ariaLabel="Go to previous page"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </PaginationButton>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((pageNum, index) => {
            if (pageNum === '...') {
              return (
                <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                  ...
                </span>
              );
            }

            return (
              <PaginationButton
                key={pageNum}
                variant="page"
                active={pageNum === page}
                onClick={() => onPageChange(pageNum as number)}
                ariaLabel={`Go to page ${pageNum}`}
              >
                {pageNum}
              </PaginationButton>
            );
          })}
        </div>

        {/* Next Page */}
        <PaginationButton
          variant="nav"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          ariaLabel="Go to next page"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </PaginationButton>

        {/* Last Page */}
        <PaginationButton
          variant="nav"
          onClick={() => onPageChange(totalPages)}
          disabled={!hasNext}
          ariaLabel="Go to last page"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
            <path
              fillRule="evenodd"
              d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </PaginationButton>
      </div>

      {/* Page Size Selector */}
      {showPageSize && onPageSizeChange && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <label htmlFor="pageSize" className="whitespace-nowrap">
            Rows per page:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 transition-colors"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default TablePagination;

